import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import AgentSelect from "./AgentSelect";
import DayOfWeekField, { DEFAULT_DAYS, scheduleText, defaultTimezone } from "./DayOfWeekField";

// What each block of the Slack message shows — mirrors the template's message on the server.
const LINES = [
  ["Emails", "sent · replies"],
  ["LinkedIn", "connections sent · accepted · messages sent · replies"],
  ["Calls", "made · connected"]
];

const CreateActivityReport = observer(() => {
  const history = useHistory();
  const [name, setName] = useState("");
  const [scope, setScope] = useState("me"); // me | team
  const [perPerson, setPerPerson] = useState(false);
  const [runWhen, setRunWhen] = useState("DAILY"); // DAILY | WEEKDAYS | WEEKLY
  const [time, setTime] = useState("09:00");
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [slackChannelId, setSlackChannelId] = useState("");
  const [saving, setSaving] = useState(false);
  const timezone = defaultTimezone();

  useEffect(() => { agentsData.fetchSlackChannels(); }, []);

  const channels = agentsData.slackChannels || [];
  const slackMissing = agentsData.slackChannelsLoaded && !channels.length;
  const channel = channels.find(c => c.id === slackChannelId);
  const splitByPerson = scope === "team" && perPerson;

  const buildPayload = () => ({
    name: name.trim() || (scope === "team" ? "Team activity report" : "Daily activity report"),
    scope,
    perPerson: splitByPerson,
    frequency: runWhen,
    time,
    ...(runWhen === "WEEKLY" ? { days } : {}),
    timezone,
    slackChannelId: slackChannelId || undefined,
    slackChannelName: channel ? channel.name : undefined
  });

  const submit = async () => {
    setSaving(true);
    const agent = await agentsData.createActivityReport(buildPayload());
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  const customize = async () => {
    setSaving(true);
    const draft = await agentsData.composeTemplate("activity-report", buildPayload());
    setSaving(false);
    if (draft) { agentsData.stageDraft(draft); history.push("/agents/new/custom"); }
  };

  const step = node => (<li><span className="ck">✓</span><span>{node}</span></li>);
  const who = scope === "team" ? (splitByPerson ? "each person on your team" : "your whole team") : "you";

  return (
    <div className="ag-page">
      <div className="ag-page__head">
        <div>
          <button className="ag-link" onClick={() => history.push("/agents/new")}>← Back</button>
          <h1 className="ag-page__title">Hire · Daily activity report</h1>
          <p className="ag-page__sub">Every morning, post yesterday's email, LinkedIn and call numbers to Slack — the same numbers as the Reports page.</p>
        </div>
      </div>

      <div className="ag-setup">
        <div className="ag-card-panel">
          <div className="ag-fsec">Whose numbers</div>
          <label className="ag-field">
            <span className="ag-field__label">Report on</span>
            <AgentSelect value={scope} onChange={setScope} options={[
              { value: "me", label: "My activity" },
              { value: "team", label: "My team's activity" }
            ]} />
          </label>
          {scope === "team" && (
            <label className="ag-check" style={{ display: "flex", alignItems: "center", gap: 8, margin: "2px 0 6px" }}>
              <input type="checkbox" checked={perPerson} onChange={e => setPerPerson(e.target.checked)} />
              <span>Show each person's numbers separately</span>
            </label>
          )}
          <label className="ag-field">
            <span className="ag-field__label">Agent name</span>
            <input className="ag-input" value={name} placeholder={scope === "team" ? "Team activity report" : "Daily activity report"}
              onChange={e => setName(e.target.value)} />
          </label>

          <div className="ag-fsec">Schedule</div>
          <div className="ag-field-row">
            <label className="ag-field"><span className="ag-field__label">Send it</span>
              <AgentSelect value={runWhen} onChange={setRunWhen} options={[
                { value: "DAILY", label: "Every day" },
                { value: "WEEKDAYS", label: "Weekdays (Mon–Fri)" },
                { value: "WEEKLY", label: "Specific days" }
              ]} />
            </label>
            {runWhen === "WEEKLY" && <DayOfWeekField value={days} onChange={setDays} />}
            <label className="ag-field"><span className="ag-field__label">At</span>
              <input className="ag-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
            </label>
          </div>
          <span className="ag-hint">Each report covers the whole of yesterday.</span>

          <div className="ag-fsec">Slack</div>
          {slackMissing ? (
            <span className="ag-hint">Slack isn't connected yet — connect it in Settings → Integrations, or the report will have nowhere to go.</span>
          ) : (
            <label className="ag-field">
              <span className="ag-field__label">Post to</span>
              <AgentSelect value={slackChannelId} searchable onChange={setSlackChannelId}
                options={[{ value: "", label: "Your team's channel" }].concat(channels.map(c => ({
                  value: c.id, label: "#" + c.name + (c.teamDefault ? " (team's channel)" : "")
                })))} />
            </label>
          )}
        </div>

        <div className="ag-rail">
          <div className="ag-summary">
            <h4>📊 What this agent does</h4>
            <ul className="ag-slist">
              {step(<span>Runs <b>{scheduleText(runWhen, time, days, timezone)}</b></span>)}
              {step(<span>Reads yesterday's numbers for <b>{who}</b></span>)}
              {step(<span>Posts them to <b>{channel ? "#" + channel.name : "your team's Slack channel"}</b></span>)}
            </ul>
            <div className="ag-hint" style={{ marginTop: 10 }}>
              {LINES.map(([k, v]) => <div key={k}><b>{k}:</b> {v}</div>)}
            </div>
          </div>
          <button className="ag-btn ag-btn--primary" style={{ justifyContent: "center" }} disabled={saving} onClick={submit}>
            {saving ? "Creating…" : "Create agent →"}
          </button>
          <button className="ag-btn" style={{ justifyContent: "center" }} disabled={saving} onClick={customize}>Customize steps →</button>
          <button className="ag-btn" style={{ justifyContent: "center" }} onClick={() => history.push("/agents")}>Cancel</button>
        </div>
      </div>
    </div>
  );
});

export default CreateActivityReport;
